import React, { useState, useEffect } from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  Select,
  HStack,
  VStack,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  useColorModeValue,
  Spinner,
  useToast,
  Flex,
  ButtonGroup,
  Badge
} from '@chakra-ui/react';
import { ChevronDownIcon, AddIcon, ViewIcon, InfoIcon } from '@chakra-ui/icons';
import api from '../../../../../services/api';
import NewKBTasksViewModal from './NewKBTasksViewModal';

/**
 * Compact task creation form for use within the call modal
 */
const NewKBTaskForm = ({ 
  createTask, 
  setCreateTask, 
  taskData, 
  setTaskData, 
  lead 
}) => {
  // Move all color mode values to the top level
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const taskFormBg = useColorModeValue('blue.50', 'blue.900');
  const inputBg = useColorModeValue('white', 'gray.700');
  
  // State for users list and task creation
  const [users, setUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isCreatingTask, setIsCreatingTask] = useState(false);
  const [isTasksModalOpen, setIsTasksModalOpen] = useState(false);
  const toast = useToast();
  
  // Fetch users when component mounts
  useEffect(() => {
    fetchUsers();
  }, []);
  
  // Fetch users from API
  const fetchUsers = async () => {
    if (isLoadingUsers) return;
    
    setIsLoadingUsers(true);
    try {
      const response = await api.get('/api/users');
      if (response.data && response.data.users) {
        setUsers(response.data.users);
      }
    } catch (error) {
      console.error('[NewKBTaskForm] Error fetching users:', error);
    } finally {
      setIsLoadingUsers(false);
    }
  };
  
  // Get ISO string from date for datetime-local input
  const getLocalDateTimeString = (date) => {
    const d = date ? new Date(date) : new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  };
  
  // Handle task creation directly from the form
  const handleCreateTask = async () => {
    // Validate task data
    if (!taskData.title.trim()) {
      toast({
        title: 'Task title required',
        description: 'Please enter a title for the task',
        status: 'error',
        duration: 3000,
      });
      return;
    }
    
    setIsCreatingTask(true);
    try {
      // Create task payload
      const taskPayload = {
        ...taskData,
        leadId: lead.leadId || lead._id,
        category: taskData.category || 'Follow-up',
        status: 'pending'
      };
      
      // Send API request
      console.log('[NewKBTaskForm] Creating task with data:', taskPayload);
      const response = await api.post('/api/tasks', taskPayload);
      
      if (response.status === 201) {
        console.log('[NewKBTaskForm] Task created successfully');
        toast({
          title: 'Task created',
          description: 'A new task has been created for this lead',
          status: 'success',
          duration: 3000,
        });
        
        // Reset form data
        setTaskData({
          title: '',
          priority: 'medium',
          dueDate: '',
          assignedTo: {
            uid: '',
            email: ''
          }
        });
      }
    } catch (error) {
      console.error('[NewKBTaskForm] Error creating task:', error);
      toast({
        title: 'Error creating task',
        description: error.message || 'Could not create task',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsCreatingTask(false);
    }
  };
  
  // Function to set preset due dates
  const setDueDatePreset = (preset) => {
    const now = new Date();
    let dueDate = new Date(now);
    
    switch(preset) {
      case 'today':
        // Today, end of business hours (6 PM)
        dueDate.setHours(18, 0, 0, 0);
        break;
      case 'tomorrow':
        dueDate.setDate(now.getDate() + 1);
        dueDate.setHours(12, 0, 0, 0); // Noon next day
        break;
      case '2days':
        dueDate.setDate(now.getDate() + 2);
        dueDate.setHours(12, 0, 0, 0);
        break;
      case '1week':
        dueDate.setDate(now.getDate() + 7);
        dueDate.setHours(12, 0, 0, 0);
        break;
      default:
        break;
    }
    
    setTaskData({
      ...taskData,
      dueDate: getLocalDateTimeString(dueDate)
    });
  };

  // Tasks View Modal (available in both views)
  const tasksViewModal = (
    <NewKBTasksViewModal 
      isOpen={isTasksModalOpen} 
      onClose={() => setIsTasksModalOpen(false)} 
      lead={lead} 
    />
  );

  // Simplified view when task creation is disabled
  if (!createTask) {
    return (
      <Box mb={3}>
        <HStack spacing={4}>
          <Checkbox 
            isChecked={createTask} 
            onChange={(e) => setCreateTask(e.target.checked)}
            colorScheme="blue"
          >
            Create task with this call
          </Checkbox>
          
          {lead && (lead.leadId || lead._id) && (
            <Button
              leftIcon={<ViewIcon />}
              size="xs"
              colorScheme="teal"
              variant="outline"
              onClick={() => setIsTasksModalOpen(true)}
            >
              View Tasks
            </Button>
          )}
        </HStack>
        {tasksViewModal}
      </Box>
    );
  }

  // Full task creation form
  return (
    <Box 
      mb={4}
      p={3}
      borderWidth="1px"
      borderRadius="md"
      borderColor={borderColor}
      bg={taskFormBg}
    >
      {tasksViewModal}
      
      <VStack spacing={3} align="stretch">
        <HStack justify="space-between" mb={2}>
          <Checkbox 
            isChecked={createTask} 
            onChange={(e) => setCreateTask(e.target.checked)}
            colorScheme="blue"
          >
            Create task with this call
          </Checkbox>
          
          {lead && (lead.leadId || lead._id) && (
            <Button
              leftIcon={<ViewIcon />}
              size="xs"
              colorScheme="teal"
              variant="outline"
              onClick={() => setIsTasksModalOpen(true)}
            >
              View Tasks
            </Button>
          )}
        </HStack>
        
        <FormControl isRequired>
          <FormLabel fontSize="sm" mb={1}>Task Title</FormLabel>
          <Input
            size="sm"
            value={taskData.title}
            onChange={(e) => setTaskData({...taskData, title: e.target.value})}
            placeholder="Enter task title (required for saving with call)"
            bg={inputBg}
            borderColor={createTask && !taskData.title.trim() ? "red.300" : undefined}
          />
        </FormControl>
        
        <HStack spacing={2} align="flex-start">
          <FormControl>
            <FormLabel fontSize="sm" mb={1}>Priority</FormLabel>
            <Select 
              size="sm"
              value={taskData.priority}
              onChange={(e) => setTaskData({...taskData, priority: e.target.value})}
              bg={inputBg}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </Select>
          </FormControl>
          
          <FormControl>
            <FormLabel fontSize="sm" mb={1}>Due Date</FormLabel>
            <HStack spacing={1}>
              <Input
                type="datetime-local"
                size="sm"
                value={taskData.dueDate || ''}
                onChange={(e) => setTaskData({...taskData, dueDate: e.target.value})}
                min={getLocalDateTimeString(new Date())}
                bg={inputBg}
              />
              <Menu>
                <MenuButton as={Button} rightIcon={<ChevronDownIcon />} size="sm">
                  <Text fontSize="xs">Set</Text>
                </MenuButton>
                <MenuList>
                  <MenuItem onClick={() => setDueDatePreset('today')}>Today</MenuItem>
                  <MenuItem onClick={() => setDueDatePreset('tomorrow')}>Tomorrow</MenuItem>
                  <MenuItem onClick={() => setDueDatePreset('2days')}>In 2 days</MenuItem>
                  <MenuItem onClick={() => setDueDatePreset('1week')}>In 1 week</MenuItem>
                </MenuList>
              </Menu>
            </HStack>
          </FormControl>
        </HStack>
        
        <FormControl>
          <FormLabel fontSize="sm" mb={1}>Assigned To</FormLabel>
          {isLoadingUsers ? (
            <HStack spacing={2}>
              <Spinner size="sm" />
              <Text fontSize="xs">Loading users...</Text>
            </HStack>
          ) : (
            <Select 
              size="sm"
              value={taskData.assignedTo?.uid || ''}
              onChange={(e) => {
                const userId = e.target.value;
                const selectedUser = users.find(user => user.uid === userId) || {};
                
                setTaskData({
                  ...taskData, 
                  assignedTo: userId ? {
                    uid: userId,
                    email: selectedUser.email || ''
                  } : {
                    uid: '',
                    email: ''
                  }
                });
              }}
              placeholder="Select assignee"
              bg={inputBg}
            >
              <option value="">Select assignee</option>
              {users.map(user => (
                <option key={user.uid} value={user.uid}>
                  {user.displayName || user.email}
                </option>
              ))}
            </Select>
          )}
        </FormControl>
        
        {/* Create Task button */}
        <Flex justifyContent="flex-end" mt={2}>
          <ButtonGroup size="sm" spacing={3}>
            <Button
              leftIcon={<AddIcon />}
              colorScheme="blue"
              onClick={handleCreateTask}
              isLoading={isCreatingTask}
              loadingText="Creating..."
              isDisabled={!taskData.title.trim()}
            >
              Create Task Now
            </Button>
          </ButtonGroup>
        </Flex>
        
        {createTask && (
          <Text fontSize="xs" color="gray.500" display="flex" alignItems="center">
            <InfoIcon mr={1} />
            {taskData.title.trim() 
              ? "This task will be saved when you click 'Save Call Record'" 
              : "Please add a title to save this task with the call record"}
          </Text>
        )}
      </VStack>
    </Box>
  );
};

export default NewKBTaskForm;
